import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  Ruler, 
  ShieldCheck, 
  Clock, 
  Leaf, 
  MapPin, 
  FileCheck, 
  ArrowRight, 
  Phone, 
  Mail, 
  Building2, 
  Home as HomeIcon,
  Layers,
  Sparkles
} from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import FAQSection from '../components/FAQSection';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Skruefundering() {
  const heroImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/skruefundament-byggeplads-montering.jpg", { width: 1400, quality: 85 });
  const processImage1 = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/jordskruer-teknisk-detalje.jpg", { width: 800, quality: 85 });
  const processImage2 = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg", { width: 800, quality: 85 });
  const applicationImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/modulbyggeri-skruefundament.jpg", { width: 800, quality: 85 });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Skruefundering - CM Skruefundament ApS",
    "description": "Professionel skruefundering fra vurdering og dimensionering til præcis montering i hele Danmark.",
    "provider": {
      "@type": "Organization",
      "name": "CM Skruefundament ApS",
      "url": "https://cmskruefundament.dk",
      "telephone": "+4526796139",
      "email": "cs@cmskruefundament.dk"
    },
    "areaServed": "Danmark"
  };

  return (
    <Layout>
      <Helmet>
        <title>Skruefundering | Fra vurdering til færdigt fundament | Kontakt</title>
        <meta 
          name="description" 
          content="Med skruefundering skaber vi et stabilt grundlag ved at skrue stålfundamenter ned i bærende jordlag. Få vurdering og montering i hele Danmark." 
        />
        <link rel="canonical" href="https://cmskruefundament.dk/skruefundering" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/skruefundering" />
        <meta property="og:title" content="Skruefundering | Fra vurdering til færdigt fundament | Kontakt" />
        <meta 
          property="og:description" 
          content="Med skruefundering skaber vi et stabilt grundlag ved at skrue stålfundamenter ned i bærende jordlag. Få vurdering og montering i hele Danmark." 
        />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CM Skruefundament ApS" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/skruefundering" />
        <meta name="twitter:title" content="Skruefundering | Fra vurdering til færdigt fundament | Kontakt" />
        <meta 
          name="twitter:description" 
          content="Med skruefundering skaber vi et stabilt grundlag ved at skrue stålfundamenter ned i bærende jordlag. Få vurdering og montering i hele Danmark." 
        />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative bg-slate-900 text-white pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Professionel skruefundering på byggeplads med specialudstyr til jordboring"
            className="w-full h-full object-cover opacity-20 filter brightness-75 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-slate-900/60" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs sm:text-sm font-bold uppercase tracking-widest mb-6">
                <Sparkles className="w-4 h-4" />
                Effektiv & Præcis Fundering
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl min-[992px]:text-[2.75rem] font-black uppercase tracking-tight text-white leading-tight mb-6">
                Skruefundering – fra vurdering til færdigt fundament
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-light mb-8">
                Med skruefundering skaber vi et stabilt grundlag ved at skrue stålfundamenter ned i de bærende jordlag. Hos <Link to="/" className="font-bold text-white underline decoration-amber-400 hover:text-amber-300 transition-colors">CM Skruefundament</Link> vurderer, dimensionerer og monterer vi løsningen til dit byggeri, så du kan komme videre uden omfattende gravearbejde og ventetid på hærdning.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#kontakt-form"
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 px-7 py-3.5 rounded-xl text-sm font-bold uppercase tracking-widest text-center transition-colors shadow-lg hover:shadow-amber-500/20 inline-flex items-center justify-center gap-2"
                >
                  Få et uforpligtende tilbud
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/skruer"
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-7 py-3.5 rounded-xl text-sm font-bold uppercase tracking-widest text-center transition-colors inline-flex items-center justify-center"
                >
                  Se vores skruer & specifikationer
                </Link>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="Skruefundering generelt"
                title="Få tilbud på skruefundering"
                subtitle="Beskriv dit byggeprojekt, så leverer vi professionel beregning og et uforpligtende prisoverslag."
              />
            </div>

          </div>
        </div>
      </section>

      {/* QUICK HIGHLIGHTS BAR */}
      <section className="bg-slate-950 border-y border-slate-800 py-6 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <span className="text-2xl lg:text-3xl font-black text-amber-400">0 timer</span>
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-1">Hærdetid</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-2xl lg:text-3xl font-black text-white">100%</span>
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-1">Laserpræcision</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-2xl lg:text-3xl font-black text-white">Minimal</span>
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-1">Jordhåndtering</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-2xl lg:text-3xl font-black text-amber-400">Hele DK</span>
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-1">Dækningsområde</span>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION: Hvad gør skruefundering til en effektiv metode */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-800 text-xs font-bold uppercase tracking-widest mb-4">
                Funderingsmetoden
              </div>
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
                Hvad gør skruefundering til en effektiv metode?
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed text-base sm:text-lg">
                <p>
                  Skruefundering er selve metoden, mens skruefundamentet er den løsning, konstruktionen hviler på. Jordskruerne overfører belastningen til jorden og kan tilpasses både jordbund og byggeri. Derfor vælger vi aldrig skruernes længde, diameter eller antal ud fra en standardløsning alene.
                </p>
                <p>
                  Sammenlignet med et traditionelt betonfundament kræver metoden normalt langt mindre udgravning og håndtering af jord. Der er heller ingen beton, som skal hærde, før arbejdet fortsætter. Når fundamentpunkterne er monteret og kontrolleret, kan den efterfølgende konstruktion påbegyndes.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded bg-amber-500/10 flex items-center justify-center text-amber-600">
                      <Layers className="w-4 h-4" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wide">Tilpasset jordlag</h3>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Overfører belastningen direkte ned i de faste, bærende lag uanset terræn.
                  </p>
                </div>

                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded bg-amber-500/10 flex items-center justify-center text-amber-600">
                      <Clock className="w-4 h-4" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wide">Ingen hærdetid</h3>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Byggeriets næste faser kan starte umiddelbart efter godkendt montering.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-amber-500/10 rounded-2xl transform rotate-1" />
                <div className="relative rounded-xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
                  <img
                    src={processImage1}
                    alt="Teknisk skruefundament med gevind og flange tilpasset projektets belastning"
                    className="w-full h-80 sm:h-96 object-cover"
                  />
                  <div className="p-6 bg-slate-900 text-white">
                    <p className="text-xs font-bold uppercase tracking-widest text-amber-400 mb-1">Skræddersyet løsning</p>
                    <p className="text-sm text-slate-300">
                      Ingen faste standardpakker – hver enkelt jordskrue dimensioneres til de specifikke belastninger.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION: Sådan udfører vi skruefundering trin for trin */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 text-amber-700 text-xs font-bold uppercase tracking-widest mb-4">
              Arbejdsprocessen
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
              Sådan udfører vi skruefundering trin for trin
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              En god løsning starter længe før den første skrue føres ned. Vi gennemgår projektet, vurderer konstruktionens belastning og ser på jordbundsforholdene. Derefter planlægger vi placering, dimensioner og montering, så fundamentet passer til det byggeri, der skal stå ovenpå.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Trin 1 kort */}
            <div className="bg-white p-8 sm:p-10 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-between hover:shadow-md transition-shadow">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-4xl font-black text-amber-500">01</span>
                  <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center text-white">
                    <Ruler className="w-6 h-6" />
                  </div>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-900 mb-4">
                  Vurdering og dimensionering før montering af jordskruer
                </h3>

                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  Før montering af <Link to="/jordskruer" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">jordskruer</Link> fastlægger vi, hvilke fundamentpunkter projektet kræver. Skruetype, længde og godstykkelse vælges efter opgaven, og ved behov kan løsningen bestå af forlængerdele. Det er især vigtigt ved større konstruktioner, hvor belastningerne stiller højere krav.
                </p>

                <div className="border-t border-slate-100 pt-6">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Centrale fokusområder:</h4>
                  <ul className="space-y-3">
                    {[
                      'Vurdering af konstruktion og jordbund',
                      'Dimensionering af skruer og fundamentpunkter',
                      'Opmåling og afsætning med laserudstyr',
                      'Plan for nedskruning og efterfølgende kontrol'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-700 font-medium text-sm">
                        <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Trin 2 kort */}
            <div className="bg-white p-8 sm:p-10 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-between hover:shadow-md transition-shadow">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-4xl font-black text-amber-500">02</span>
                  <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center text-white">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-900 mb-4">
                  Præcis montering med kontrol af bæreevnen
                </h3>

                <div className="space-y-4 text-slate-600 text-base leading-relaxed mb-6">
                  <p>
                    Fundamentpunkterne afsættes med laser, hvorefter skruerne føres ned med specialmaskiner til de bærende jordlag. Under arbejdet kontrollerer vi hver skrues bæreevne. Det giver et fagligt grundlag for at vurdere, om den valgte løsning svarer til projektets krav.
                  </p>
                  <p>
                    Når skruerne er placeret og kontrolleret, er fundamentet klar til remme, bjælker eller moduler. Du undgår den tørretid, som følger med beton, og byggeprocessen kan fortsætte med det samme. Samtidig begrænses indgrebet i haven eller på byggepladsen.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-lg overflow-hidden border border-slate-100">
                <img
                  src={processImage2}
                  alt="Kontrol af bæreevne og præcis montering af jordskruer med momentmåling"
                  className="w-full h-44 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION: Jordskruer til fundament for hus og tilbygning */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl border border-slate-200">
                  <img
                    src={applicationImage}
                    alt="Skruefundament til hus, sommerhus og modulbyggeri med dokumenteret bæreevne"
                    className="w-full h-80 sm:h-96 object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white p-6 rounded-xl shadow-xl hidden sm:block max-w-xs border border-slate-800">
                  <Building2 className="w-8 h-8 text-amber-400 mb-2" />
                  <p className="text-xs uppercase font-bold tracking-widest text-amber-400 mb-1">Skånsom montage</p>
                  <p className="text-xs text-slate-300">Ideelt til tilbygninger tæt på eksisterende bygninger uden at ødelægge haven.</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-800 text-xs font-bold uppercase tracking-widest mb-4">
                Boliger & Tilbygninger
              </div>
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
                Jordskruer til fundament for hus og tilbygning
              </h2>

              <div className="space-y-4 text-slate-600 leading-relaxed text-base sm:text-lg">
                <p>
                  Jordskruer til fundament kan bruges under blandt andet skure, <Link to="/terrasseskruer" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">terrasser</Link>, sommerhuse og modulbyggeri. Det skruefundament huset skal stå på, dimensioneres altid ud fra de konkrete laster, jordbundsforhold og tekniske projektkrav. Her er et produktvalg fra hylden ikke nok.
                </p>
                <p>
                  Det skruefundament tilbygningen skal hvile på, kræver samme grundige planlægning. Løsningen kan være relevant, når du ønsker præcis fundering med begrænset gravearbejde tæt ved en eksisterende bygning. Vi tilpasser placeringen til den nye konstruktion og forholdene på stedet.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-8 border-t border-slate-200">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <HomeIcon className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800 block">Sommerhus</span>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <Building2 className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800 block">Tilbygning</span>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <Layers className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800 block">Modulbyggeri</span>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <ShieldCheck className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800 block">Helårshus</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION: Derfor vælger mange skruefundering frem for beton */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-widest mb-4">
              Fordele & Sammenligning
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white mb-6">
              Derfor vælger mange skruefundering frem for beton
            </h2>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Den største forskel ligger i selve byggeprocessen. Betonfundering indebærer typisk udgravning, forskalling, støbning og hærdetid. Ved skruefundering monteres fundamentet direkte i jorden, så du får færre arbejdsprocesser og kan planlægge den videre montage uden lang ventetid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              {
                icon: Leaf,
                title: 'Begrænset gravearbejde',
                desc: 'Mindre håndtering af jord, ingen bortkørsel af store jordmængder og minimal påvirkning af terræn.'
              },
              {
                icon: Clock,
                title: 'Ingen ventetid',
                desc: 'Ingen forsinkende hærdeproces på beton. Byg videre direkte ovenpå monterede fundamentpunkter.'
              },
              {
                icon: Ruler,
                title: 'Præcis afsætning',
                desc: 'Hvert fundamentpunkt afsættes med laserudstyr med millimeterpræcision for perfekt pasform.'
              },
              {
                icon: FileCheck,
                title: 'Bæreevnekontrol',
                desc: 'Dokumenteret styrke og kontinuerlig kontrol af bæreevnen under selve monteringen.'
              }
            ].map((feature, i) => (
              <div key={i} className="bg-slate-800/80 border border-slate-700/80 p-6 rounded-xl">
                <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mb-4">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wide text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Citat / Genanvendelse boks */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-850 p-8 rounded-xl border border-slate-700 max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex-shrink-0 flex items-center justify-center text-slate-950 font-black">
                ✓
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Stålskruerne kan desuden tages op igen og indgå i genanvendelse. Det kan gøre løsningen mere ressourcebevidst end en traditionel støbning, men den konkrete miljøfordel afhænger altid af projektet. Vi rådgiver derfor ud fra byggeri, jordbund og ønsket anvendelse frem for brede standardløfter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <FAQSection
        badgeText="Ofte stillede spørgsmål"
        title="Spørgsmål & svar om skruefundering"
        description="Få grundige svar på de vigtigste spørgsmål om bæreevne, jordbundsforhold, tidsforbrug og fordelene ved skruefundamenter."
        themeColor="amber"
        bgClass="bg-white"
        items={[
          {
            question: "Hvordan beregnes bæreevnen ved skruefundering?",
            answer: `Bæreevnen for et skruefundament fastlægges ud fra en kombination af konstruktionens samlede vægt og belastning, jordbundens geotekniske sammensætning samt skruens specifikke dimensioner og gevindflader. Under selve nedskruningen overvåger og måler vi det hydrauliske tilspændingsmoment (drejningsmoment), hvilket giver en direkte og dokumenteret indikation af den opnåede bæreevne i det pågældende jordlag.

Ved større eller mere komplekse byggeprojekter kan der desuden udføres prøvebelastninger eller trækprøver på stedet for at sikre, at fundamentet overholder alle gældende ingeniørmæssige normer og sikkerhedsfaktorer.`
          },
          {
            question: "Hvilke typer jordbund egner skruefundering sig til?",
            answer: `Skruefundering fungerer fremragende i langt de fleste danske jordtyper, herunder sandjord, lerjord, muldjord, grus samt tørveholdige eller opfyldte områder med varierende bæreevne. Skruerne kan forlænges med specialudviklede forlængerstykker, så vi kan nå ned igennem bløde overfladelag og forankre sikkert i faste, bærende jordlag i frostfri dybde – ofte 1,5 til 3 meter eller dybere efter behov.

Skulle vi støde på massive underjordiske kampesten eller fast grundfjeld, råder vi over specialiseret forboringsudstyr, som kan gennembryde hårde lag eller muliggøre alternativ placering.`
          },
          {
            question: "Hvor hurtigt kan man bygge videre på et skruefundament i forhold til beton?",
            answer: `En af de absolut største fordele ved skruefundering er, at fundamentet er 100% belastningsklart i samme sekund, den sidste skrue er monteret i vatter. I modsætning til traditionel støbning af betonrande eller punktfundamenter, hvor man typisk skal afvente hærdning i adskillige dage eller uger, er der ingen tørretid eller fugtudvikling forbundet med skruefundamenter.

Håndværkerne eller du selv kan derfor påbegynde opsætning af træskelet, bjælkelag eller modulkonstruktioner med det samme, hvilket forkorter den samlede byggeperiode markant.`
          },
          {
            question: "Hvad er levetiden og holdbarheden på et skruefundament i stål?",
            answer: `Professionelle jordskruer til fundamentering fremstilles af konstruktionsstål af høj kvalitet og er varmgalvaniseret i henhold til gældende europæiske standarder (herunder ISO 1461). Den kraftige zinkbelægning giver en optimal og langtidsholdbar korrosionsbeskyttelse mod jordens fugtighed og mineralske påvirkninger, hvilket sikrer en forventet teknisk levetid på 50 til 100 år afhængig af jordens aggressive miljø.

Skruefundamenter anvendes derfor i dag som en permanent og fuldt anerkendt funderingsmetode til både helårsboliger, tilbygninger og erhvervsbyggeri.`
          },
          {
            question: "Er skruefundering et mere miljøvenligt valg end traditionel beton?",
            answer: `Ja, skruefundering har en række markante miljømæssige fordele, da fremstillingen af stålfundamenter og montagen generelt udleder væsentligt mindre CO₂ end udvinding, transport og støbning af store mængder beton. Derudover kræver processen intet omfattende gravearbejde, hvilket betyder, at der ikke skal bortskaffes eller deponeres tung jord fra byggegrunden, og den omkringliggende natur og havearealer bevares uforstyrret.

Skulle konstruktionen en dag flyttes eller fjernes, kan jordskruerne let skrues op igen og enten genanvendes på et nyt projekt eller omsmeltes 100% som genbrugsstål.`
          }
        ]}
      />

      {/* SEKTION: Klar til skruefundering i hele Danmark + Kontaktform */}
      <section id="kontakt" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Info venstre side */}
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 text-amber-700 text-xs font-bold uppercase tracking-widest mb-4">
                <MapPin className="w-3.5 h-3.5" />
                Rådgivning & Montering
              </div>
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
                Klar til skruefundering i hele Danmark
              </h2>

              <div className="space-y-4 text-slate-600 text-base sm:text-lg leading-relaxed mb-8">
                <p>
                  Fra vores base i Horsens udfører CM Skruefundament skruefundering i Jylland, på Fyn og på Sjælland. Vi hjælper både private, virksomheder og professionelle byggeaktører med rådgivning, dimensionering og montage. Læs mere <Link to="/om-os" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">om os</Link>. Hvis du selv står for arbejdet, hjælper vi også med relevante produkter som <Link to="/groundplug" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">GroundPlug</Link> og <Link to="/jordanker" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">jordanker</Link>.
                </p>
                <p>
                  Fortæl os, hvad du skal bygge, hvor konstruktionen skal placeres, og hvilket projektmateriale du allerede har. Så kan vi tage en faglig dialog om jordbund, belastning og montering af jordskruer. Du får et uforpligtende tilbud på en løsning, der er tilpasset dit byggeri.
                </p>
              </div>

              {/* Direkte kontaktbokse */}
              <div className="space-y-4">
                <a
                  href="tel:+4526796139"
                  className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-amber-500 transition-colors shadow-sm"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Ring direkte</p>
                    <p className="text-lg font-bold text-slate-900">26 79 61 39</p>
                  </div>
                </a>

                <a
                  href="mailto:cs@cmskruefundament.dk"
                  className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-amber-500 transition-colors shadow-sm"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Send os en email</p>
                    <p className="text-base font-bold text-slate-900">cs@cmskruefundament.dk</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Kontaktformular højre side */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 sm:p-10 rounded-2xl shadow-xl border border-slate-200">
                <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-900 mb-2">
                  Få et uforpligtende tilbud
                </h3>
                <p className="text-slate-500 text-sm mb-8">
                  Udfyld formularen med dine projektoplysninger, så vender vi hurtigt tilbage med en skræddersyet beregning.
                </p>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
